/* eslint-disable arrow-body-style */
/* eslint-disable max-len */
/* eslint-disable camelcase */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import throttle from 'lodash.throttle';

import useOnScreen from 'shared/hooks/useOnScreen';

/**
 * Позволяет порционно рендерить элементы-слоты каждые timeStamp миллисекунд пока triggerRef находится в зоне видимости.
 * Это позволяет предотвратить рендер сразу всех елементов-слотов, которых может быть очень много, и соответственно задоджить лаги.
 * Например когда на одной странице сразу все слоты сразу всех провайдеров.
 * @param allItems - все слоты, которые нужно отрендерить
 * @param timeStamp - промежуток между рендерами новых порций
 * @param itemsIncrementCount - число слотов в одной порции
 * @param basedCount - базовое количество слотов в первой порции
 * @param deps - специфические зависимости для сброса подгруженного списка
 * @param delay - задержка перед началом подгрузки - нужна как предохранитель от преждевременного старта подгрузки,
 * например когда верстка еще не прогрузилась как нужно и тригер преждевременно оказывается в зоне видимости
 * @returns items - слоты готовые к рендеру. triggerRef - реф, который нужно навесить на элемент тригер.
 * Тригер - это невидимый элемент(как правило на дне блока со слотами) который пока он на экране запускает подгрузку новых порций.
 */

export interface IUseDynamicItemsRenderingProps<TItem> {
  allItems: TItem[];
  timeStamp?: number;
  itemsIncrementCount?: number;
  basedCount?: number;
  deps?: string;
  delay?: number;
}

const useDynamicItemsRendering = <TItem>({
  allItems,
  timeStamp = 500,
  itemsIncrementCount = 10,
  basedCount = 10,
  deps = '',
  delay = 300,
}: IUseDynamicItemsRenderingProps<TItem>) => {
  const [delayEnd, setDelayEnd] = useState(() => {
    setTimeout(() => setDelayEnd(true), delay);
    return false;
  });
  const [items, setItems] = useState<TItem[]>([]);

  const throttled = useRef(
    throttle((setItems2, allItems2) => {
      setItems2((prevItems: TItem[]) => {
        return [...allItems2.slice(0, prevItems.length + itemsIncrementCount)];
      });
    }, timeStamp),
  );

  // сброс списка видимых элементов
  // условие сброса можно явно определить извне через deps, иначе сброс будет при любых структурных изменениях исходного списка
  useEffect(() => {
    throttled.current.cancel();
    setItems(() => allItems.slice(0, basedCount));

    return () => {
      // eslint-disable-next-line react-hooks/exhaustive-deps
      throttled.current.cancel();
      setItems(() => []);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deps || JSON.stringify(allItems), basedCount]);

  const triggerRef = useRef<HTMLElement>();
  const isTriggerVisible = useOnScreen({
    targetRef: triggerRef,
    observerOptions: {
      threshold: [0.5],
    },
  });

  if (isTriggerVisible && items.length < allItems.length && delayEnd) {
    throttled.current(setItems, allItems);
  }

  // eslint-disable-next-line arrow-body-style
  return useMemo(() => {
    return { visibleItems: items, triggerRef: triggerRef as React.LegacyRef<HTMLDivElement> };
  }, [items]);
};

export default useDynamicItemsRendering;
